import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { 
  Plus, Download, Eye, Trash2, Calendar, Clock, 
  Upload, Loader2, ChevronDown, ChevronUp, File, Paperclip,
  Pencil, Save, X, ArrowUpDown, ArrowUp, ArrowDown, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import { adjuntosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Formatear tamaño de archivo
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Componente para mostrar adjuntos de una actuación
const ActuacionAdjuntos = ({ actuacionId, adjuntos, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo_adjunto', 'documento');
      
      try {
        await adjuntosAPI.uploadActuacion(actuacionId, formData);
        successCount++;
      } catch (error) {
        console.error('Error subiendo archivo:', error);
        toast({
          title: "Error",
          description: `No se pudo subir ${file.name}`,
          variant: "destructive"
        });
      }
    }
    
    if (successCount > 0) {
      toast({ title: `${successCount} archivo(s) subido(s)` });
      onUpdate();
    }
    setUploading(false);
  };

  const handleDownload = async (adjunto) => {
    try {
      const response = await adjuntosAPI.download(adjunto.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', adjunto.nombre);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo descargar", variant: "destructive" });
    }
  };

  const handleDelete = async (adjuntoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await adjuntosAPI.delete(adjuntoId);
      toast({ title: "Archivo eliminado" });
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  if (!adjuntos || adjuntos.length === 0) {
    return (
      <div className="mt-3 pt-3 border-t">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <Paperclip className="w-4 h-4" /> Sin adjuntos
          </span>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          />
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Adjuntar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <Paperclip className="w-4 h-4" /> Adjuntos ({adjuntos.length})
        </span>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          onChange={(e) => handleUpload(Array.from(e.target.files || []))}
        />
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>
      <div className="space-y-1">
        {adjuntos.map((adj) => (
          <div key={adj.id} className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-sm">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <button
                onClick={() => window.open(`/preview/${adj.id}`, '_blank')}
                className="text-blue-600 hover:underline truncate"
                title={adj.nombre}
              >
                {adj.nombre}
              </button>
              <span className="text-gray-400 text-xs flex-shrink-0">
                ({formatFileSize(adj.tamano)})
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0"
                onClick={() => window.open(`/preview/${adj.id}`, '_blank')}
                title="Previsualizar"
              >
                <Eye className="w-4 h-4 text-green-600" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0"
                onClick={() => handleDownload(adj)}
                title="Descargar"
              >
                <Download className="w-4 h-4 text-blue-600" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0"
                onClick={() => handleDelete(adj.id)}
                title="Eliminar"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Componente principal de Actuaciones
const ActuacionesSection = ({ expedienteId, actuaciones, onCreateActuacion, onRefresh }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingActuacion, setEditingActuacion] = useState(null);
  const [observaciones, setObservaciones] = useState('');
  const [editObservaciones, setEditObservaciones] = useState('');
  const [adjuntosTemp, setAdjuntosTemp] = useState([]);
  const [saving, setSaving] = useState(false);
  const [actuacionesConAdjuntos, setActuacionesConAdjuntos] = useState({});
  const [expandedActuaciones, setExpandedActuaciones] = useState(new Set());
  const fileInputRef = useRef(null);
  
  // Estado para ordenación
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' });

  // Función para cambiar el ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Obtener icono de ordenación
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" /> 
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  // Actuaciones ordenadas
  const sortedActuaciones = useMemo(() => {
    if (!actuaciones || actuaciones.length === 0) return [];
    
    return [...actuaciones].sort((a, b) => {
      const { key, direction } = sortConfig;
      let comparison = 0;
      
      if (key === 'fecha') {
        const dateA = new Date(a.fecha || 0);
        const dateB = new Date(b.fecha || 0);
        comparison = dateA - dateB;
      } else if (key === 'titulo') {
        const titleA = (a.titulo || '').toLowerCase();
        const titleB = (b.titulo || '').toLowerCase();
        comparison = titleA.localeCompare(titleB, 'es');
      }
      
      return direction === 'asc' ? comparison : -comparison;
    });
  }, [actuaciones, sortConfig]);

  // Cargar adjuntos de todas las actuaciones
  const loadAdjuntosActuaciones = useCallback(async () => {
    const adjuntosMap = {};
    for (const act of actuaciones) {
      try {
        const response = await adjuntosAPI.getByActuacion(act.id);
        adjuntosMap[act.id] = response.data;
      } catch (error) {
        adjuntosMap[act.id] = [];
      }
    }
    setActuacionesConAdjuntos(adjuntosMap);
  }, [actuaciones]);

  useEffect(() => {
    if (actuaciones.length > 0) {
      loadAdjuntosActuaciones();
    }
  }, [actuaciones, loadAdjuntosActuaciones]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const formData = new FormData(e.target);
    const data = {
      titulo: formData.get('titulo') || 'Sin título',
      fecha: new Date(formData.get('fecha')).toISOString(),
      tipo: 'general',
      descripcion: observaciones || 'Sin descripción',
      observaciones: observaciones
    };
    
    try {
      const newActuacion = await onCreateActuacion(data);
      
      if (adjuntosTemp.length > 0 && newActuacion?.id) {
        for (const file of adjuntosTemp) {
          const uploadData = new FormData();
          uploadData.append('file', file);
          uploadData.append('tipo_adjunto', 'documento');
          try {
            await adjuntosAPI.uploadActuacion(newActuacion.id, uploadData);
          } catch (err) {
            console.error('Error subiendo adjunto:', err);
          }
        }
      }
      
      setIsDialogOpen(false);
      setObservaciones('');
      setAdjuntosTemp([]);
      onRefresh();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear la actuación", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Editar actuación
  const handleEditClick = (act, e) => {
    e.stopPropagation();
    setEditingActuacion(act);
    setEditObservaciones(act.observaciones || act.descripcion || '');
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const formData = new FormData(e.target);
    const data = {
      titulo: formData.get('titulo') || editingActuacion.titulo,
      tipo: editingActuacion.tipo || 'general',
      descripcion: editObservaciones || editingActuacion.descripcion,
      observaciones: editObservaciones,
      fecha: new Date(formData.get('fecha')).toISOString()
    };
    
    try {
      await expedientesAPI.updateActuacion(editingActuacion.id, data);
      toast({ title: "Actuación actualizada" });
      setIsEditDialogOpen(false);
      setEditingActuacion(null);
      setEditObservaciones('');
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      toast({ title: "Error", description: "No se pudo actualizar la actuación", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Eliminar actuación
  const handleDeleteClick = async (act, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la actuación "${act.titulo}"?`)) return;
    
    try {
      await expedientesAPI.deleteActuacion(act.id);
      toast({ title: "Actuación eliminada" });
      onRefresh();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar la actuación", variant: "destructive" });
    }
  };

  const handleAddTempFiles = (files) => {
    setAdjuntosTemp(prev => [...prev, ...Array.from(files)]);
  };

  const handleRemoveTempFile = (index) => {
    setAdjuntosTemp(prev => prev.filter((_, i) => i !== index));
  };

  const toggleActuacion = (id) => {
    setExpandedActuaciones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        {/* Dialog Nueva Actuación */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800">
              <Plus className="w-4 h-4 mr-2" />NUEVA ACTUACIÓN
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nueva actuación</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Label>Título *</Label>
                  <Input name="titulo" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha</Label>
                    <Input name="fecha" type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div>
                    <Label>Duración</Label>
                    <Input name="duracion" type="time" step="1" />
                  </div>
                </div>
                
                <div data-color-mode="light">
                  <Label>Observaciones</Label>
                  <div className="mt-1">
                    <MDEditor
                      value={observaciones}
                      onChange={setObservaciones}
                      preview="edit"
                      height={200}
                      textareaProps={{
                        placeholder: 'Escribe las observaciones... (soporta formato Markdown)'
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" /> Archivos adjuntos
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    multiple
                    onChange={(e) => handleAddTempFiles(e.target.files)}
                  />
                  <div 
                    className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                      const files = e.dataTransfer.files;
                      if (files && files.length > 0) {
                        handleAddTempFiles(files);
                      }
                    }}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Arrastra archivos aquí o haz clic para seleccionar</p>
                    <p className="text-xs text-gray-400 mt-1">Puedes seleccionar múltiples archivos</p>
                  </div>
                  
                  {adjuntosTemp.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Archivos a adjuntar:</p>
                      {adjuntosTemp.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1 px-2 bg-gray-100 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{file.name}</span>
                            <span className="text-gray-400 text-xs">({formatFileSize(file.size)})</span>
                          </div>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => handleRemoveTempFile(idx)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => {
                  setIsDialogOpen(false);
                  setObservaciones('');
                  setAdjuntosTemp([]);
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Editar Actuación */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar actuación</DialogTitle>
            </DialogHeader>
            {editingActuacion && (
              <form onSubmit={handleEditSubmit}>
                <div className="space-y-4">
                  <div>
                    <Label>Título *</Label>
                    <Input name="titulo" defaultValue={editingActuacion.titulo} required />
                  </div>
                  <div>
                    <Label>Fecha</Label>
                    <Input 
                      name="fecha" 
                      type="date" 
                      defaultValue={editingActuacion.fecha?.split('T')[0] || ''} 
                    />
                  </div>
                  
                  <div data-color-mode="light">
                    <Label>Observaciones</Label>
                    <div className="mt-1">
                      <MDEditor
                        value={editObservaciones}
                        onChange={setEditObservaciones}
                        preview="edit"
                        height={200}
                      />
                    </div>
                  </div>
                </div>
                
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => {
                    setIsEditDialogOpen(false);
                    setEditingActuacion(null);
                    setEditObservaciones('');
                  }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Guardar cambios
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Lista de actuaciones */}
      <div className="space-y-3">
        {/* Controles de ordenación */}
        {actuaciones.length > 0 && (
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
            <span className="text-sm font-medium text-gray-700">Ordenar por:</span>
            <button
              onClick={() => handleSort('fecha')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                sortConfig.key === 'fecha' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Fecha
              {getSortIcon('fecha')}
            </button>
            <button
              onClick={() => handleSort('titulo')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                sortConfig.key === 'titulo' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <FileText className="w-4 h-4" />
              Título
              {getSortIcon('titulo')}
            </button>
            <span className="ml-auto text-xs text-gray-500">
              {actuaciones.length} actuación{actuaciones.length !== 1 ? 'es' : ''}
            </span>
          </div>
        )}
        
        {sortedActuaciones.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No hay actuaciones registradas</p>
        ) : (
          sortedActuaciones.map((act) => {
            const isExpanded = expandedActuaciones.has(act.id);
            const adjuntos = actuacionesConAdjuntos[act.id] || [];
            
            return (
              <div key={act.id} className="border rounded-lg overflow-hidden">
                <div 
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleActuacion(act.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        {act.titulo}
                        {adjuntos.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            <Paperclip className="w-3 h-3 mr-1" />
                            {adjuntos.length}
                          </Badge>
                        )}
                      </h4>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(act.fecha).toLocaleDateString('es-ES')}
                        </span>
                        {act.duracion && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {act.duracion}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Botones de editar y eliminar */}
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={(e) => handleEditClick(act, e)}
                        title="Editar actuación"
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={(e) => handleDeleteClick(act, e)}
                        title="Eliminar actuación"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    {(act.observaciones || act.descripcion) && (
                      <div className="mt-3" data-color-mode="light">
                        <p className="text-sm font-medium text-gray-700 mb-2">Observaciones:</p>
                        <div className="bg-white p-3 rounded border prose prose-sm max-w-none">
                          <MDEditor.Markdown source={act.observaciones || act.descripcion} />
                        </div>
                      </div>
                    )}
                    
                    <ActuacionAdjuntos 
                      actuacionId={act.id}
                      adjuntos={adjuntos}
                      onUpdate={loadAdjuntosActuaciones}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActuacionesSection;
